import currency from 'currency.js'

/**
 * Calculator service to calculate tax amounts based on price and tax inputs.
 */
class TaxCalculator {
  static #instance = null

  constructor () {
    if (!TaxCalculator.#instance) {
      TaxCalculator.#instance = this
    }
    return TaxCalculator.#instance
  }

  /**
   * Calculate total and individual tax amounts from a net price.
   * @param {*} price
   * @param {*} taxes
   * @returns
   */
  net (price, taxes) {
    const taxAmounts = []
    let totalTaxAmount = 0

    _.each(taxes, tax => {
      const taxAmount = this.#taxFromNet(price, tax.percentThousandths)
      taxAmounts.push({
        tax_id: tax.id,
        tax_name: tax.name,
        tax_percent_thousandths: tax.percentThousandths,
        tax_amount: taxAmount
      })
      totalTaxAmount = currency(totalTaxAmount).add(taxAmount).value
    })

    return {
      taxAmount: totalTaxAmount,
      taxes: taxAmounts
    }
  }

  /**
   * Calculate total and individual tax amounts from a gross price.
   * @param {*} price
   * @param {*} taxes
   * @returns
   */
  gross (price, taxes) {
    const taxAmounts = []
    let totalTaxAmount = 0
    const totalTaxPercentThousandths = this.#totalTaxPercentThousandths(taxes)

    _.each(taxes, tax => {
      const taxAmount = this.#taxFromGross(price, tax.percentThousandths, totalTaxPercentThousandths)
      taxAmounts.push({
        tax_id: tax.id,
        tax_name: tax.name,
        tax_percent_thousandths: tax.percentThousandths,
        tax_amount: taxAmount
      })
      totalTaxAmount = currency(totalTaxAmount).add(taxAmount).value
    })

    return {
      taxAmount: totalTaxAmount,
      taxes: taxAmounts
    }
  }

  /**
   * Calculate corrected total and individual tax amounts from given expected and uncorrected prices.
   * @param {*} expectedPrice
   * @param {*} uncorrectedPrice
   * @param {*} taxes
   * @returns
   */
  correction (expectedPrice, uncorrectedPrice, taxes) {
    const taxCorrections = []
    let totalTaxCorrection = 0
    const totalTaxPercentThousandths = this.#totalTaxPercentThousandths(taxes)
    _.each(taxes, tax => {
      const uncorrectedTaxAmount = this.#taxFromGross(uncorrectedPrice, tax.percentThousandths, totalTaxPercentThousandths)
      const correctedTaxAmount = this.#taxFromGross(expectedPrice, tax.percentThousandths, totalTaxPercentThousandths)
      const taxCorrection = currency(uncorrectedTaxAmount).subtract(correctedTaxAmount).value

      taxCorrections.push({
        tax_id: tax.id,
        tax_name: tax.name,
        tax_percent_thousandths: tax.percentThousandths,
        tax_amount: correctedTaxAmount,
        tax_correction: taxCorrection
      })
      totalTaxCorrection = currency(totalTaxCorrection).add(taxCorrection).value
    })

    return {
      correction: totalTaxCorrection,
      taxes: taxCorrections
    }
  }

  // private

  #totalTaxPercentThousandths (taxes) {
    return _.sumBy(taxes, 'percentThousandths')
  }

  /**
   * Compute a single rounded tax amount from a given gross price (rounds partial cent values).
   * @param {*} gross
   * @param {*} taxPercentThousandths
   * @param {*} totalTaxPercentThousandths
   * @returns
   */
  #taxFromGross (gross, taxPercentThousandths, totalTaxPercentThousandths) {
    const netPricePercentThousandths = 100000 // 100% of price: 100 (percent) * 1000 (percent thousandths)
    // I assume JS won't fail when trying to add two whole numbers
    const grossPercentThousandths = (netPricePercentThousandths + totalTaxPercentThousandths) // Total Gross % (100% Price + X% Tax)
    const oneThousandthTaxValue = currency(gross).divide(grossPercentThousandths).value // 0.1% tax amount
    const unroundedTaxAmount = currency(oneThousandthTaxValue).multiply(taxPercentThousandths).value // Use 0.1% value to get tax value
    return unroundedTaxAmount
  }

  /**
   * Compute a single rounded tax amount from a given net price (rounds partial cent values).
   * @param {*} net
   * @param {*} taxPercentThousandths
   * @returns
   */
  #taxFromNet (net, taxPercentThousandths) {
    const netPricePercentThousandths = 100000 // 100% of price: 100 (percent) * 1000 (percent thousandths)
    const unroundedTaxAmount = currency(net).multiply(taxPercentThousandths).divide(netPricePercentThousandths).value
    return unroundedTaxAmount
  }
}

const taxCalculator = new TaxCalculator()
export default taxCalculator
